import { Box, Card, CardBody, Icon, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { useAuthAdmin } from '../AuthProviderAdmin';
import { useAdminNavItems, type AdminNavItem } from '../adminNavItems';
import { testIds } from './testIds';

type Group = {
  heading: string;
  links: string[];
};

export const HomePage = () => {
  const { user } = useAuthAdmin();
  const navItems = useAdminNavItems();

  const groupedItems = useMemo(() => {
    const groups: Group[] = [
      {
        heading: t`Work needing attention`,
        links: ['/support', '/tasks', '/assessments'],
      },
      {
        heading: t`People records`,
        links: ['/students', '/enrolments', '/providers', '/placements'],
      },
      {
        heading: t`Catalogue`,
        links: ['/courses', '/units'],
      },
      {
        heading: t`Money & compliance`,
        links: ['/finances/invoices', '/avetmiss'],
      },
      {
        heading: t`System`,
        links: ['/users', '/developer-tools'],
      },
    ];

    const byLink = new Map(navItems.map((item) => [item.link, item]));
    return groups
      .map((group) => ({
        heading: group.heading,
        items: group.links
          .map((link) => byLink.get(link))
          .filter((item): item is AdminNavItem => item != null),
      }))
      .filter((group) => group.items.length > 0);
  }, [navItems]);

  return (
    <Stack gap="0" h="full" w="full" bg="bg.surface">
      <Box p="4">
        <PageHeading data-testid={testIds.heading}>
          <Trans>Welcome{user?.first_name == null ? '' : `, ${user.first_name}`}</Trans>
        </PageHeading>
      </Box>

      <Stack gap="6" px="4" pb="6">
        {groupedItems.map((group) => (
          <Stack key={group.heading} gap="3">
            <Text as="h2" fontSize="md" fontWeight="semibold">
              {group.heading}
            </Text>
            <SimpleGrid columns={{ base: 2, md: 3 }} gap="4">
              {group.items.map((item) => (
                <Card
                  key={item.link}
                  as={Link}
                  to={item.link}
                  w={{ base: 'full' }}
                  _hover={{ shadow: 'md', borderColor: 'brand.500' }}
                  transition="box-shadow 0.15s, border-color 0.15s"
                  cursor="pointer"
                  borderWidth="1px"
                  borderColor="border.default"
                >
                  <CardBody>
                    <Stack gap="2">
                      <Icon as={item.icon} boxSize={iconSize.xl} color="brand.500" />
                      <Text fontWeight="semibold">{item.label}</Text>
                      <Text fontSize="sm" color="fg.muted">
                        {item.description}
                      </Text>
                    </Stack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
